/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  Autocomplete, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl,
  FormControlLabel, FormGroup, Grid, IconButton, InputAdornment, InputLabel,
  OutlinedInput, styled, TextField, Tooltip, Typography
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import { useSelector } from "react-redux";
import { apiRequest } from "../common/common";
import { showNotification } from "../common/Notification/ToastifyNotification";

const CustomFilterPanelWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  padding: 10,
});

const FilterContent = styled(Box)({
  flexGrow: 1,
  overflowY: 'auto',
  paddingBottom: 30,
  paddingTop: 10
});

const FilterFooter = styled(Box)({
  position: 'sticky',
  bottom: 0,
  display: 'flex',
  gap: 5
});

const FixedButton = styled(Button)({
  width: '100%',
});

export default function ColumnPanel({
  columns,
  setColumns,
  settingData,
  fetchData,
  type,
  defaultVisibleColumns,
  columnOrder
}) {
  const { user } = useSelector((state) => state.users);
  const [column, setColumn] = useState([]);
  const [filterColumn, setFilterColumn] = useState(columns);
  const [name, setName] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [viewId, setViewId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [selectedOption, setSelectedOption] = React.useState(null);

  useEffect(() => {
    if (settingData?.find(a => a.is_pinned)?.columns.length > 0) {
      setColumn(settingData?.find(a => a.is_pinned)?.columns);
      setViewId(settingData?.find(a => a.is_pinned)?.view_id);
    } else {
      setColumn(columns);
    }
  }, [settingData]);

  const handleSaveNew = async () => {
    if (!name) {
      return showNotification('Please enter a setting name before save', 'error');
    }
    
    const payload = {
      view_name: name,
      description: `${name} for ${type}`,
      columns: columnOrder.map((a) => {
        return {
          ...a,
          hide: column.find((d) => d.field === a.field).hide,
          pinned: column.find((d) => d.field === a.field).pinned,
          lockPinned: column.find((d) => d.field === a.field).lockPinned
        }
      }),
      user_id: user?.user_id,
      organization_id: user?.organization?.organization_id,
      type,
      is_pinned: true
    };

    try {
      setIsLoading(true);
      const response = await apiRequest('post', '/user-view', payload);
      if (response?.data) {
        setColumns(response?.data?.data?.columns);
        setName('');
        setIsNew(false);
        fetchData();
        setIsLoading(false);
        showNotification(response?.data?.message, 'success');
      }
    } catch (error) {
      showNotification(error?.response?.data?.error || error.message, 'error');
    }
  };

  const handleUpdate = async () => {
    if (!viewId) {
      return showNotification('Please select a setting before applying', 'error');
    }
    
    const payload = {
      is_pinned: true,
      columns: columnOrder.map((a) => {
        return {
          ...a,
          hide: column.find((d) => d.field === a.field).hide,
          pinned: column.find((d) => d.field === a.field).pinned,
          lockPinned: column.find((d) => d.field === a.field).lockPinned
        }
      }),
      user_id: user?.user_id,
      organization_id: user?.organization?.organization_id,
      type
    };

    try {
      setIsLoading(true);
      const response = await apiRequest('put', `/user-view/${viewId}`, payload);
      if (response?.data) {
        setColumns(response?.data?.data?.columns);
        fetchData();
        showNotification(response?.data?.message, 'success');
        setIsLoading(false);
      }
    } catch (error) {
      showNotification(error?.response?.data?.error || error.message, 'error');
    }
  };

  const handleDeleteClick = (option) => {
    setSelectedOption(option);
    setOpenDialog(true);  // Open the confirmation dialog
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedOption(null);
  };

  const handleConfirmDelete = async () => {
    try {
      setIsLoading(true);
      const response = await apiRequest('delete', `/user-view/${selectedOption.view_id}`);
      if (response?.data) {
        if (viewId === selectedOption.view_id) {
          setColumn(defaultVisibleColumns);
          setColumns(defaultVisibleColumns);
          setViewId(null);
        }
        fetchData();
        showNotification(response?.data?.message, 'success');
        setOpenDialog(false);
        setSelectedOption(null);
        setIsLoading(false);
      }
    } catch (error) {
      showNotification(error?.response?.data?.error || error.message, 'error');
    }
  };

  return (
    <CustomFilterPanelWrapper>
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete the setting: <strong>{selectedOption?.view_name}</strong>?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary" variant="contained">
            No
          </Button>
          <Button onClick={handleConfirmDelete} color="secondary" variant="contained">
            Yes
          </Button>
        </DialogActions>
      </Dialog>
      <Box display="flex" justifyContent="space-between" mb={1}>
        <Typography variant="body1" style={{ fontWeight: 'bold' }}>Table Settings</Typography>
        {viewId && (
          <Tooltip title="Back to default setting">
            <SettingsBackupRestoreIcon color="primary" style={{ cursor: 'pointer' }}
              onClick={async () => {
                const payload = {
                  is_pinned: false,
                  columns: column,
                  user_id: user?.user_id,
                  organization_id: user?.organization?.organization_id,
                  type,
                };

                try {
                  setIsLoading(true);
                  const response = await apiRequest('put', `/user-view/${viewId}`, payload);
                  if (response?.data) {
                    setColumn(defaultVisibleColumns);
                    setColumns(defaultVisibleColumns);
                    setViewId(null);
                    fetchData();
                    showNotification('Reset to default setting successfully', 'success');
                    setIsLoading(false);
                  }
                } catch (error) {
                  showNotification(error?.response?.data?.error || error.message, 'error');
                }
              }} />
          </Tooltip>
        )}
      </Box>
      <Divider />
      <FilterContent>
        <Typography variant="body2" style={{ fontWeight: 'bold' }}>Setting</Typography>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 5, margin: '10px 0px' }}>
          {!isNew ? (
            <Autocomplete
              value={settingData?.find((c) => c?.view_id === viewId) || {}}
              size="small"
              clearIcon={null}
              style={{ width: 280 }}
              options={settingData}
              getOptionLabel={(option) => option?.view_name || ''}
              onChange={(event, newValue) => {
                setColumn(newValue?.columns || defaultVisibleColumns);
                setColumns(newValue?.columns);
                setViewId(newValue?.view_id);
              }}
              renderOption={(props, option) => (
                <li {...props}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <span>{option?.view_name}</span>
                    <IconButton onClick={() => handleDeleteClick(option)}>
                      <DeleteIcon color="secondary" />
                    </IconButton>
                  </div>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Select Setting" variant="outlined" />
              )}
            />
          ) : (
            <FormControl size="small" variant="outlined" style={{ width: 250 }}>
              <InputLabel>Setting Name</InputLabel>
              <OutlinedInput
                label="Setting Name"
                value={name || ''}
                onChange={(e) => setName(e.target.value)}
              />
            </FormControl>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={!isNew && <AddIcon />}
            style={{ height: 39, textTransform: 'capitalize', width: isNew ? 150 : 120 }}
            onClick={() => {
              setIsNew(!isNew);
              setName('');
            }}
          >
            {isNew ? 'Select Existing' : 'Add New'}
          </Button>
        </Box>

        {/* Available Columns */}
        <Typography variant="body2" style={{ fontWeight: 'bold' }}>Columns</Typography>
        <Typography variant="body2" style={{ fontSize: 12 }}>Choose which columns to display.</Typography>
        <Divider />
        <Grid container>
          {/* Available */}
          <Grid item sm={6} md={6} lg={6} style={{ borderRight: '1px solid lightgray', paddingTop: 5 }}>
            <Typography variant="body2" style={{ fontWeight: 'bold' }}>Available Columns</Typography>
            <TextField
              size="small"
              placeholder="Search"
              style={{ background: '#ebeaea', marginBottom: 5, width: 205 }}
              onChange={(e) => {
                const val = e.target.value.trim().toLowerCase();
                if (val) {
                  setFilterColumn(columns?.filter((a) => a.headerName.toLowerCase().includes(val)));
                } else {
                  setFilterColumn(columns);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    style={{ padding: '1px 10px' }}
                    indeterminate={
                      column.filter(c => !c.hide).length > 0 &&
                      column.length > column.filter(c => !c.hide).length
                    }
                    checked={column.length === column.filter(c => !c.hide).length}
                    onClick={(e) => {
                      const checked = e.target.checked;
                      setColumn(prev =>
                        prev.map(item => ({ ...item, hide: !checked }))
                      );
                    }}
                  />
                }
                label="Select All"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
              />
            </FormGroup>
            {type === 'project-item' ? (
              <>
                {filterColumn.slice(0, 107)?.map((a, index) => (
                  <FormGroup key={index}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          style={{ padding: '1px 10px' }}
                          checked={!column.find(c => c.headerName === a.headerName)?.hide}
                          onClick={(e) => {
                            const checked = e.target.checked;
                            setColumn(prev =>
                              prev.map(item =>
                                item.headerName === a.headerName
                                  ? { ...item, hide: !checked }
                                  : item
                              )
                            );
                          }}
                        />
                      }
                      label={a.headerName}
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '13px' } }}
                    />
                  </FormGroup>
                ))}
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>Procurement</Typography>
                {filterColumn.slice(107, 111)?.map((a, index) => (
                  <FormGroup key={index}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          style={{ padding: '1px 10px' }}
                          checked={!column.find(c => c.headerName === a.headerName)?.hide}
                          onClick={(e) => {
                            const checked = e.target.checked;
                            setColumn(prev =>
                              prev.map(item =>
                                item.headerName === a.headerName
                                  ? { ...item, hide: !checked }
                                  : item
                              )
                            );
                          }}
                        />
                      }
                      label={a.headerName}
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '13px' } }}
                    />
                  </FormGroup>
                ))}
              </>
            ) : (
              filterColumn?.map((a, index) => (
                <FormGroup key={index}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        style={{ padding: '1px 10px' }}
                        checked={!column.find(c => c.headerName === a.headerName)?.hide}
                        onClick={(e) => {
                          const checked = e.target.checked;
                          setColumn(prev =>
                            prev.map(item =>
                              item.headerName === a.headerName
                                ? { ...item, hide: !checked }
                                : item
                            )
                          );
                        }}
                      />
                    }
                    label={a.headerName}
                    sx={{ '& .MuiFormControlLabel-label': { fontSize: '13px' } }}
                  />
                </FormGroup>
              ))
            )}
          </Grid>

          {/* Selected */}
          <Grid item sm={5} md={5} lg={5} style={{ marginLeft: 10, paddingTop: 5 }}>
            <Typography variant="body2" style={{ fontWeight: 'bold' }}>Selected Columns</Typography>
            <Typography variant="body2" style={{ fontSize: 12 }}>Pin to Freeze column</Typography>
            {column?.filter(c => !c.hide)?.map((a, idx) => (
              <Box key={idx} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '2px 10px' }}>
                {a.lockPinned ? (
                  <PushPinRoundedIcon
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      setColumn(prev =>
                        prev.map(item =>
                          item.headerName === a.headerName
                            ? { ...item, lockPinned: false, pinned: undefined }
                            : item
                        )
                      )
                    }
                  />
                ) : (
                  <PushPinOutlinedIcon
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      setColumn(prev =>
                        prev.map(item =>
                          item.headerName === a.headerName
                            ? { ...item, pinned: 'left', lockPinned: true }
                            : item
                        )
                      )
                    }
                  />
                )}
                <Typography variant="body2" style={{ fontSize: 12 }}>{a.headerName}</Typography>
              </Box>
            ))}
          </Grid>
        </Grid>
      </FilterContent>

      {/* Footer */}
      <FilterFooter>
        <FixedButton
          variant="contained"
          color="secondary"
          disabled={!isNew || isLoading}
          startIcon={isLoading && isNew ? (<CircularProgress size="small" color="primary" />) : ''}
          onClick={handleSaveNew}
        >
          Save As New
        </FixedButton>
        <FixedButton
          variant="contained"
          color="primary"
          disabled={isLoading}
          startIcon={isLoading ? (<CircularProgress size="small" color="primary" />) : ''}
          onClick={handleUpdate}
        >
          Save And Apply
        </FixedButton>
      </FilterFooter>
    </CustomFilterPanelWrapper>
  );
}
